/**
 * Serialized units — row shapes for migration 142.
 *
 * One `serialized_units` row per physical finished good with a barcode, and
 * an append-only `serialized_unit_events` timeline underneath it.
 */

export type SerializedUnitStatus = 'built' | 'on_hold' | 'scrapped' | 'shipped' | 'returned';
export type QcResult = 'pass' | 'fail';

export const UNIT_STATUSES: Array<{ value: SerializedUnitStatus; label: string }> = [
  { value: 'built', label: 'Built' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'returned', label: 'Returned' },
  { value: 'scrapped', label: 'Scrapped' },
];

export const UNIT_STATUS_LABEL: Record<SerializedUnitStatus, string> = {
  built: 'Built',
  on_hold: 'On hold',
  scrapped: 'Scrapped',
  shipped: 'Shipped',
  returned: 'Returned',
};

/** Badge variant per status, shared by the Unit log table and the unit sheet. */
export const UNIT_STATUS_VARIANT: Record<SerializedUnitStatus, 'success-soft' | 'warning-soft' | 'info-soft' | 'muted' | 'outline'> = {
  built: 'success-soft',
  on_hold: 'warning-soft',
  shipped: 'info-soft',
  returned: 'outline',
  scrapped: 'muted',
};

/** One component lot inside a unit, as confirmed at the bench. */
export interface ComponentLot {
  item_id: string | null;
  sku: string;
  name: string;
  lot_number: string;
  lot_id: string | null;
}

export interface SerializedUnit {
  id: string;
  organization_id: string;
  serial_number: string;
  item_id: string;
  status: SerializedUnitStatus;
  built_at: string;
  built_by: string | null;
  workstation_id: string | null;
  work_session_id: string | null;
  work_unit_id: string | null;
  production_form_id: string | null;
  lot_id: string | null;
  build_seconds: number | null;
  qc_result: QcResult | null;
  qc_by: string | null;
  qc_at: string | null;
  qc_notes: string | null;
  label_photo_path: string | null;
  photo_paths: string[];
  component_lots: ComponentLot[];
  notes: string | null;
  sales_order_id: string | null;
  shipment_id: string | null;
  customer_id: string | null;
  destination: string | null;
  shipped_at: string | null;
  shipped_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Unit row with the joins the log table shows. */
export interface SerializedUnitRow extends SerializedUnit {
  items: { id: string; sku: string; name: string } | null;
  builder: { id: string; full_name: string | null; email: string } | null;
  inspector: { id: string; full_name: string | null; email: string } | null;
  workstations: { id: string; name: string } | null;
  customers: { id: string; name: string } | null;
  sales_orders: { id: string; so_number: string; ship_to: ShipTo | null } | null;
  shipments: { id: string; shipment_number: string; tracking_number: string | null } | null;
}

export interface ShipTo {
  name?: string | null;
  city?: string | null;
  province?: string | null;
  country?: string | null;
  country_code?: string | null;
}

export type SerializedUnitEventKind =
  | 'built'
  | 'registered'
  | 'qc'
  | 'status'
  | 'photos'
  | 'lots'
  | 'note'
  | 'shipped'
  | 'returned';

export interface SerializedUnitEvent {
  id: string;
  organization_id: string;
  unit_id: string;
  kind: SerializedUnitEventKind | string;
  actor_id: string | null;
  note: string | null;
  data: Record<string, unknown>;
  created_at: string;
}

export interface SerializedUnitDetail {
  unit: SerializedUnit;
  events: SerializedUnitEvent[];
}

/** Return shape of `serialized_units_ship`. */
export interface ShipUnitsResult {
  shipped: string[];
  not_found: string[];
  already_shipped: string[];
  blocked: Array<{ serial: string; status: SerializedUnitStatus }>;
}

/** Return row of `work_component_lots_on_hand`. */
export interface ComponentLotOnHand {
  item_id: string;
  lot_id: string;
  lot_number: string;
  on_hand: number;
  received_at: string;
}

/** Unit photos required before the bench will log a serialized unit. */
export const SERIAL_MIN_PHOTOS = 2;
export const SERIAL_MAX_PHOTOS = 8;

export function personLabel(p: { full_name: string | null; email: string } | null | undefined): string {
  if (!p) return '—';
  return p.full_name || p.email;
}

/** "Hamburg, Germany" from a sales order's ship-to block. */
export function shipToPlace(s: ShipTo | null | undefined): string | null {
  if (!s) return null;
  const parts = [s.city, s.province, s.country ?? s.country_code].filter((x): x is string => !!x && x.trim() !== '');
  return parts.length ? parts.join(', ') : null;
}

/** Where a unit went, in one line. */
export function unitDestination(u: SerializedUnitRow): string | null {
  if (u.status !== 'shipped' && u.status !== 'returned' && !u.destination && !u.customers && !u.sales_orders) return null;
  const bits = [
    u.customers?.name ?? null,
    u.destination ?? shipToPlace(u.sales_orders?.ship_to) ?? null,
    u.sales_orders?.so_number ? `SO ${u.sales_orders.so_number}` : null,
  ].filter((x): x is string => !!x);
  return bits.length ? bits.join(' · ') : null;
}
